import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSessionStatus } from './session-status';

export type SessionPolicy = {
  idleTimeoutHours: number;
  warningMinutes: number;
};

export type SessionPolicyState = {
  /** Absent until the session answers — the organization window is the only window a countdown may run on. */
  policy?: SessionPolicy;
  /** The server row's expiry, converted to this browser's clock the moment the answer arrived. */
  expiresAt?: number;
  extend: () => Promise<boolean>;
};

export function useSessionPolicy(): SessionPolicyState {
  const [policy, setPolicy] = useState<SessionPolicy | undefined>(undefined);
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);
  const loadedRef = useRef(false);

  const read = useCallback(async (extend: boolean): Promise<boolean> => {
    let authenticated = false;
    // Allowed try/catch: a transient network failure must degrade to a value here,
    // never throw out of the timer callback that asked whether the session still lives.
    try {
      const status = await fetchSessionStatus(window.fetch.bind(window), { extend: extend });
      if (typeof status.idleTimeoutHours === 'number' && typeof status.warningMinutes === 'number') {
        setPolicy({ idleTimeoutHours: status.idleTimeoutHours, warningMinutes: status.warningMinutes });
      }
      if (typeof status.expiresInMs === 'number') {
        setExpiresAt(Date.now() + status.expiresInMs);
      }
      authenticated = status.authenticated;
    } catch {
      authenticated = false;
    }
    return authenticated;
  }, []);

  useEffect(() => {
    // The session clock is the trigger, not a user event: every tab needs the organization's
    // window before it can schedule a warning, and only the leader tab runs the status poll.
    if (!loadedRef.current && typeof window !== 'undefined') {
      loadedRef.current = true;
      void read(false);
    }
  }, [read]);

  const extend = useCallback(async (): Promise<boolean> => {
    return read(true);
  }, [read]);

  return {
    policy: policy,
    expiresAt: expiresAt,
    extend: extend,
  };
}
